import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Dropdown, Form, InputGroup, Row } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  FaArrowLeft, FaCalculator, FaColumns, FaFileExcel, FaFileImport, FaFilePdf, FaPlus,
  FaSearch, FaTimes, FaTrash,
} from 'react-icons/fa';
import { DEFAULT_PARAMS, DEFAULT_BAREME_IR, INDEMNITE_ORDER, computeEmploye } from './netBrutEngine';

const INPUT_BG  = '#fdf6ee';
const HEADER_BG = 'rgb(245,245,219)';
const STORAGE_KEY = 'net_brut_calculator_state_v2';

const fmt2   = (v) => Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const sumCol = (arr, key) => arr.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);

let nextId = 1;
const blankEmploye = () => ({ id: nextId++, nom: '', statut: 'M', net_cible: '', anciennete: 1, jours_travailles: 26, autres_deductions: 0, mutuelle_salarie: 0, personnes_charge: 0, representation: false, cimr: false, avantages_nature: 0, stc: 0 });

// ─── Persistance localStorage (Infinity n'est pas JSON-serialisable nativement) ──
const INFINITY_TAG = '__Infinity__';
const loadSavedState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw, (k, v) => v === INFINITY_TAG ? Infinity : v);
  } catch { return null; }
};
const saveState = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state, (k, v) => v === Infinity ? INFINITY_TAG : v));
  } catch { /* quota dépassé / stockage indisponible */ }
};
// Fusionne les paramètres sauvegardés avec les valeurs par défaut (ajoute les nouveaux champs si absents)
const mergeParams = (saved) => {
  const merged = structuredClone(DEFAULT_PARAMS);
  if (!saved) return merged;
  for (const key of Object.keys(merged)) {
    if (saved[key] === undefined) continue;
    if (typeof merged[key] === 'object' && merged[key] !== null) {
      for (const subkey of Object.keys(merged[key])) {
        if (saved[key][subkey] === undefined) continue;
        if (typeof merged[key][subkey] === 'object' && merged[key][subkey] !== null) {
          merged[key][subkey] = { ...merged[key][subkey], ...saved[key][subkey] };
        } else {
          merged[key][subkey] = saved[key][subkey];
        }
      }
    } else {
      merged[key] = saved[key];
    }
  }
  return merged;
};

const COMPUTED_COLS = [
  { key: 'brut',             label: 'Brut',             defaultOn: true  },
  { key: 'cnss',             label: 'CNSS',              defaultOn: false },
  { key: 'amo',              label: 'AMO',               defaultOn: false },
  { key: 'fraisPro',         label: 'Frais pro',         defaultOn: false },
  { key: 'baseIR',           label: 'Base IR',           defaultOn: false },
  { key: 'ir',               label: 'IR',                defaultOn: true  },
  { key: 'cimrMontant',      label: 'CIMR',              defaultOn: false },
  { key: 'indTransport',     label: 'Ind. transport',    defaultOn: false },
  { key: 'indSalissure',     label: 'Ind. salissure',    defaultOn: false },
  { key: 'indOutillage',     label: 'Ind. outillage',    defaultOn: false },
  { key: 'indPanier',        label: 'Ind. panier',       defaultOn: false },
  { key: 'indCaisse',        label: 'Ind. caisse',       defaultOn: false },
  { key: 'indLait',          label: 'Ind. lait',         defaultOn: false },
  { key: 'indTournee',       label: 'Ind. tournée',      defaultOn: false },
  { key: 'indRepresentation', label: 'Ind. représentation', defaultOn: false },
  { key: 'indemnitesTotal',  label: 'Total ind.',        defaultOn: false },
  { key: 'primeRendement',        label: 'Prime de rendement',        defaultOn: true  },
  { key: 'netRecalcule',     label: 'Net recalculé',     defaultOn: true  },
  { key: 'ecart',            label: 'Écart',             defaultOn: true  },
  { key: 'chargesPatronales', label: 'Charges patronales', defaultOn: false },
  { key: 'coutEmployeur',    label: 'Coût employeur',    defaultOn: false },
];

const PAGE_SIZES  = [10, 25, 50, 100];
const normSearch  = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const thStyle     = { position: 'sticky', top: 0, zIndex: 2, background: HEADER_BG };
const thInputStyle = { ...thStyle, background: INPUT_BG };

// ─── Onglet Paramètres ────────────────────────────────────────────────────────

const CHARGES_PATRONALES_LABELS = {
  cnss_patronale: 'CNSS patronale (plafonnée)',
  allocations_familiales: 'Allocations familiales',
  formation_professionnelle: 'Taxe formation professionnelle',
  amo_patronale: 'AMO patronale',
};

const ParametresTab = ({ params, bareme, onParam, onIndemnite, onRepresentation, onMutuellePatronale, onCimr, onChargePatronale, onBareme, onReset }) => (
  <div>
    <Alert variant="info" className="py-2">
      Ces paramètres pilotent tout le calcul. Modifiez-les si la législation change —
      le résultat est recalculé instantanément.
    </Alert>
    <div className="d-flex justify-content-end mb-2">
      <Button size="sm" variant="outline-secondary" onClick={onReset}>Réinitialiser aux valeurs par défaut</Button>
    </div>

    <Row className="g-3 mb-4">
      <Col md={4}><Form.Group><Form.Label>Taux CNSS salarié</Form.Label>
        <Form.Control type="number" step="0.0001" value={params.cnss_taux} onChange={(e) => onParam('cnss_taux', e.target.value)} /></Form.Group></Col>
      <Col md={4}><Form.Group><Form.Label>Plafond brut CNSS (DH)</Form.Label>
        <Form.Control type="number" step="0.01" value={params.cnss_plafond_brut} onChange={(e) => onParam('cnss_plafond_brut', e.target.value)} /></Form.Group></Col>
      <Col md={4}><Form.Group><Form.Label>CNSS montant max (DH)</Form.Label>
        <Form.Control type="number" step="0.01" value={params.cnss_montant_max} onChange={(e) => onParam('cnss_montant_max', e.target.value)} /></Form.Group></Col>
      <Col md={4}><Form.Group><Form.Label>Taux AMO salarié</Form.Label>
        <Form.Control type="number" step="0.0001" value={params.amo_taux} onChange={(e) => onParam('amo_taux', e.target.value)} /></Form.Group></Col>
      <Col xs={12}>
        <Form.Label className="fw-semibold mb-1">Frais professionnels — barème mensuel</Form.Label>
        <div className="table-responsive" style={{ maxWidth: 560 }}>
          <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: '0.875rem' }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: 200 }}>SBI mensuel (DH)</th>
                <th style={{ width: 110 }}>Taux FP</th>
                <th style={{ width: 150 }}>Plafond/mois (DH)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  0 → <Form.Control type="number" step="0.01" size="sm"
                    value={params.fraispro_seuil}
                    onChange={(e) => onParam('fraispro_seuil', e.target.value)}
                    style={{ width: 80, display: 'inline-block' }} />
                </td>
                <td><Form.Control type="number" step="0.001" size="sm"
                  value={params.fraispro_taux}
                  onChange={(e) => onParam('fraispro_taux', e.target.value)} /></td>
                <td><Form.Control type="number" step="0.01" size="sm"
                  value={params.fraispro_plafond}
                  onChange={(e) => onParam('fraispro_plafond', e.target.value)} /></td>
              </tr>
              <tr>
                <td className="text-muted">&gt; {Number(params.fraispro_seuil).toLocaleString('fr-FR')} → +</td>
                <td><Form.Control type="number" step="0.001" size="sm"
                  value={params.fraispro_taux2}
                  onChange={(e) => onParam('fraispro_taux2', e.target.value)} /></td>
                <td><Form.Control type="number" step="0.01" size="sm"
                  value={params.fraispro_plafond2}
                  onChange={(e) => onParam('fraispro_plafond2', e.target.value)} /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Col>
      <Col md={4}><Form.Group><Form.Label>SMIG brut (DH)</Form.Label>
        <Form.Control type="number" step="0.01" value={params.smig_brut} onChange={(e) => onParam('smig_brut', e.target.value)} /></Form.Group></Col>
      <Col md={4}><Form.Group><Form.Label>SMIG net (DH)</Form.Label>
        <Form.Control type="number" step="0.01" value={params.net_smig} onChange={(e) => onParam('net_smig', e.target.value)} /></Form.Group></Col>
    </Row>

    <h6 className="mb-2">Réduction IR — charges de famille (LF 2026)</h6>
    <Row className="g-3 mb-4">
      <Col md={4}><Form.Group>
        <Form.Label>Montant par personne à charge (DH/mois)</Form.Label>
        <Form.Control type="number" step="0.01" value={params.charge_famille_montant} onChange={(e) => onParam('charge_famille_montant', e.target.value)} />
      </Form.Group></Col>
      <Col md={4}><Form.Group>
        <Form.Label>Nombre maximum de personnes à charge</Form.Label>
        <Form.Control type="number" step="1" value={params.charge_famille_max} onChange={(e) => onParam('charge_famille_max', e.target.value)} />
      </Form.Group></Col>
    </Row>

    <h6 className="mb-2">Indemnités à plafond fixe — Type A (sans justificatif)</h6>
    <Alert variant="warning" className="py-2 small mb-3">
      Ordre de remplissage du gap (net_cible − SMIG net) : selon l'ordre ci-dessous, parmi les indemnités <strong>activées</strong>.
      La première activée se remplit sans seuil ; les suivantes seulement si le reste du gap &gt; 50 DH.
    </Alert>
    <Row className="g-3 mb-4">
      {INDEMNITE_ORDER.map((key) => {
        const conf = params.indemnites[key];
        return (
          <Col md={6} lg={3} key={key}>
            <Form.Group>
              <div className="d-flex align-items-center justify-content-between mb-1">
                <Form.Label className="mb-0 small">{conf.label}</Form.Label>
                <Form.Check
                  type="switch"
                  id={`ind-actif-${key}`}
                  checked={conf.actif}
                  onChange={(e) => onIndemnite(key, 'actif', e.target.checked)}
                  title="Activer / désactiver cette indemnité"
                />
              </div>
              <InputGroup size="sm">
                <Form.Control
                  type="number" step="0.01"
                  value={conf.plafond}
                  disabled={!conf.actif}
                  onChange={(e) => onIndemnite(key, 'plafond', e.target.value)}
                />
                <InputGroup.Text>DH</InputGroup.Text>
              </InputGroup>
            </Form.Group>
          </Col>
        );
      })}
    </Row>

    <h6 className="mb-2">Indemnité de représentation (cadres dirigeants)</h6>
    <Alert variant="warning" className="py-2 small mb-3">
      = % du salaire brut. À cocher individuellement par employé (colonne "Représentation" dans l'onglet Employés) — uniquement si activée ici.
    </Alert>
    <Row className="g-3 mb-4">
      <Col md={6} lg={3}>
        <Form.Group>
          <div className="d-flex align-items-center justify-content-between mb-1">
            <Form.Label className="mb-0 small">Représentation (% du brut)</Form.Label>
            <Form.Check
              type="switch"
              id="representation-actif"
              checked={params.representation.actif}
              onChange={(e) => onRepresentation('actif', e.target.checked)}
              title="Activer / désactiver l'indemnité de représentation"
            />
          </div>
          <InputGroup size="sm">
            <Form.Control
              type="number" step="0.01"
              value={params.representation.taux * 100}
              disabled={!params.representation.actif}
              onChange={(e) => onRepresentation('taux', Number(e.target.value) / 100)}
            />
            <InputGroup.Text>%</InputGroup.Text>
          </InputGroup>
        </Form.Group>
      </Col>
    </Row>

    <h6 className="mb-2">CIMR (retraite complémentaire facultative)</h6>
    <Alert variant="warning" className="py-2 small mb-3">
      Cotisation salarié, % du brut plafonnable — déductible de l'IR (réduit AX et le net).
      À cocher individuellement par employé (colonne "CIMR" dans l'onglet Employés) — uniquement si activée ici.
    </Alert>
    <Row className="g-3 mb-4">
      <Col md={6} lg={3}>
        <Form.Group>
          <div className="d-flex align-items-center justify-content-between mb-1">
            <Form.Label className="mb-0 small">CIMR (% du brut)</Form.Label>
            <Form.Check
              type="switch"
              id="cimr-actif"
              checked={params.cimr.actif}
              onChange={(e) => onCimr('actif', e.target.checked)}
              title="Activer / désactiver la CIMR"
            />
          </div>
          <InputGroup size="sm">
            <Form.Control
              type="number" step="0.01"
              value={params.cimr.taux * 100}
              disabled={!params.cimr.actif}
              onChange={(e) => onCimr('taux', Number(e.target.value) / 100)}
            />
            <InputGroup.Text>%</InputGroup.Text>
          </InputGroup>
        </Form.Group>
      </Col>
      <Col md={6} lg={3}>
        <Form.Group>
          <Form.Label className="mb-1 small">Plafond CIMR (DH/mois)</Form.Label>
          <InputGroup size="sm">
            <Form.Control
              type="number" step="0.01"
              value={params.cimr.plafond === Infinity ? '' : params.cimr.plafond}
              placeholder="Illimité"
              disabled={!params.cimr.actif}
              onChange={(e) => onCimr('plafond', e.target.value === '' ? Infinity : Number(e.target.value))}
            />
            <InputGroup.Text>DH</InputGroup.Text>
          </InputGroup>
        </Form.Group>
      </Col>
    </Row>

    <h6 className="mb-2">Charges patronales (coût employeur)</h6>
    <Alert variant="warning" className="py-2 small mb-3">
      Charges hors bulletin du salarié — n'affectent ni le net, ni l'IR, ni le brut.
      Servent uniquement à calculer la colonne informative "Coût employeur" (= Brut + charges patronales).
    </Alert>
    <Row className="g-3 mb-4">
      {Object.entries(CHARGES_PATRONALES_LABELS).map(([key, label]) => {
        const conf = params.charges_patronales[key];
        return (
          <Col md={6} lg={3} key={key}>
            <Form.Group>
              <div className="d-flex align-items-center justify-content-between mb-1">
                <Form.Label className="mb-0 small">{label}</Form.Label>
                <Form.Check
                  type="switch"
                  id={`cp-actif-${key}`}
                  checked={conf.actif}
                  onChange={(e) => onChargePatronale(key, 'actif', e.target.checked)}
                  title="Activer / désactiver cette charge patronale"
                />
              </div>
              <InputGroup size="sm">
                <Form.Control
                  type="number" step="0.01"
                  value={conf.taux * 100}
                  disabled={!conf.actif}
                  onChange={(e) => onChargePatronale(key, 'taux', Number(e.target.value) / 100)}
                />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
            </Form.Group>
          </Col>
        );
      })}
      <Col md={6} lg={3}>
        <Form.Group>
          <div className="d-flex align-items-center justify-content-between mb-1">
            <Form.Label className="mb-0 small">Mutuelle patronale (% du brut)</Form.Label>
            <Form.Check
              type="switch"
              id="mutuelle-patronale-actif"
              checked={params.mutuelle_patronale.actif}
              onChange={(e) => onMutuellePatronale('actif', e.target.checked)}
              title="Activer / désactiver la mutuelle patronale"
            />
          </div>
          <InputGroup size="sm">
            <Form.Control
              type="number" step="0.01"
              value={params.mutuelle_patronale.taux * 100}
              disabled={!params.mutuelle_patronale.actif}
              onChange={(e) => onMutuellePatronale('taux', Number(e.target.value) / 100)}
            />
            <InputGroup.Text>%</InputGroup.Text>
          </InputGroup>
        </Form.Group>
      </Col>
    </Row>

    <h6 className="mb-2">Barème IR mensuel (sur la base imposable AX)</h6>
    <div className="table-container mb-2">
      <table className="table table-hover table-striped mb-0">
        <thead><tr><th>Seuil haut (DH)</th><th>Taux</th><th>Déduction (DH)</th></tr></thead>
        <tbody>
          {bareme.map((row, idx) => (
            <tr key={idx}>
              <td>
                {row[0] === Infinity
                  ? <span className="text-muted">∞ (dernière tranche)</span>
                  : <Form.Control size="sm" type="number" step="0.01" value={row[0]} onChange={(e) => onBareme(idx, 0, e.target.value)} />}
              </td>
              <td><Form.Control size="sm" type="number" step="0.01" value={row[1]} onChange={(e) => onBareme(idx, 1, e.target.value)} /></td>
              <td><Form.Control size="sm" type="number" step="0.01" value={row[2]} onChange={(e) => onBareme(idx, 2, e.target.value)} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ─── Onglet Employés ──────────────────────────────────────────────────────────

const EmployesTab = ({ results, onAdd, onRemove, onUpdate, onTemplate, onImport, onExport, onExportSmig, onClear, onPdf, netSmig }) => {
  const fileRef = useRef(null);

  const [search,        setSearch]        = useState('');
  const [statutFilter,  setStatutFilter]  = useState('');
  const [ecartOnly,     setEcartOnly]     = useState(false);
  const [smigOnly,      setSmigOnly]      = useState(false);
  const [missingOnly,   setMissingOnly]   = useState(false);
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(25);
  const [visibleCols,  setVisibleCols]  = useState(() => new Set(COMPUTED_COLS.filter(c => c.defaultOn).map(c => c.key)));

  const sf = (setter) => (val) => { setter(val); setPage(1); };
  const toggleCol = (key) => setVisibleCols(prev => {
    const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next;
  });
  const vis = (key) => visibleCols.has(key);

  const filtered = useMemo(() => {
    const txt = normSearch(search);
    return results.filter(r => {
      if (txt && !normSearch(r.nom).includes(txt)) return false;
      if (statutFilter && r.statut !== statutFilter) return false;
      if (ecartOnly && Math.abs(r.ecart) <= 1) return false;
      if (smigOnly && !r.belowSmig) return false;
      if (missingOnly && !r.missingNet && !r.missingJours) return false;
      return true;
    });
  }, [results, search, statutFilter, ecartOnly, smigOnly, missingOnly]);

  const errCount = useMemo(
    () => results.filter(r => Number(r.net_cible) > 0 && Math.abs(r.ecart) > 1).length,
    [results]
  );

  const smigCount = useMemo(
    () => results.filter(r => r.belowSmig).length,
    [results]
  );

  const missingCount = useMemo(
    () => results.filter(r => r.missingNet || r.missingJours).length,
    [results]
  );

  const totals = useMemo(() => ({
    brut: sumCol(filtered, 'brutDisplay'), cnss: sumCol(filtered, 'cnss'), amo: sumCol(filtered, 'amo'),
    fraisPro: sumCol(filtered, 'fraisPro'), baseIR: sumCol(filtered, 'baseIR'), ir: sumCol(filtered, 'ir'),
    cimrMontant: sumCol(filtered, 'cimrMontant'),
    indTransport: sumCol(filtered, 'indTransport'), indSalissure: sumCol(filtered, 'indSalissure'),
    indOutillage: sumCol(filtered, 'indOutillage'), indPanier: sumCol(filtered, 'indPanier'),
    indCaisse: sumCol(filtered, 'indCaisse'), indLait: sumCol(filtered, 'indLait'),
    indTournee: sumCol(filtered, 'indTournee'), indRepresentation: sumCol(filtered, 'indRepresentation'),
    indemnitesTotal: sumCol(filtered, 'indemnitesTotal'),
    primeRendement: sumCol(filtered, 'primeRendement'),
    netRecalcule: sumCol(filtered, 'netRecalcule'), ecart: sumCol(filtered, 'ecart'),
    chargesPatronales: sumCol(filtered, 'chargesPatronales'),
    coutEmployeur: sumCol(filtered, 'coutEmployeur'),
  }), [filtered]);

  const totalPages  = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paged       = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const goPage      = (p) => setPage(Math.max(1, Math.min(totalPages, p)));
  const visCount    = COMPUTED_COLS.filter(c => visibleCols.has(c.key)).length;
  const colCount    = 13 + visCount; // 12 fixed + N computed + 1 action

  const dash = <span className="text-muted">—</span>;

  return (
    <div>
      {/* ① Bannière écarts */}
      {errCount > 0 && !ecartOnly && (
        <Alert variant="warning" className="py-2 d-flex align-items-center justify-content-between mb-2">
          <span>⚠ {errCount} ligne{errCount > 1 ? 's' : ''} avec écart &gt; 1 DH</span>
          <Button size="sm" variant="outline-warning" onClick={() => sf(setEcartOnly)(true)}>Voir uniquement</Button>
        </Alert>
      )}

      {/* ① bis Bannière net cible < SMIG */}
      {smigCount > 0 && !smigOnly && (
        <Alert variant="danger" className="py-2 d-flex align-items-center justify-content-between mb-2">
          <span>⚠ {smigCount} ligne{smigCount > 1 ? 's ont' : ' a'} un net cible inférieur au SMIG net ({fmt2(netSmig)} DH/mois, proratisé selon les jours travaillés)</span>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-danger" onClick={onExportSmig}><FaFileExcel className="me-1" />Télécharger</Button>
            <Button size="sm" variant="outline-danger" onClick={() => sf(setSmigOnly)(true)}>Voir uniquement</Button>
          </div>
        </Alert>
      )}

      {/* ① ter Bannière données manquantes */}
      {missingCount > 0 && !missingOnly && (
        <Alert className="py-2 d-flex align-items-center justify-content-between mb-2"
          style={{ background: '#fff3e0', borderColor: '#fb8c00', color: '#bf360c' }}>
          <span>⚠ {missingCount} ligne{missingCount > 1 ? 's' : ''} sans net cible ou sans jours travaillés (horaires)</span>
          <Button size="sm" onClick={() => sf(setMissingOnly)(true)}
            style={{ color: '#e65100', borderColor: '#fb8c00', background: 'transparent' }}>Voir uniquement</Button>
        </Alert>
      )}

      {/* ── Barre d'actions ── */}
      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
        <Button size="sm" variant="primary" onClick={onAdd}><FaPlus className="me-1" />Ajouter un employé</Button>
        <Button size="sm" variant="outline-secondary" onClick={onTemplate}><FaFileExcel className="me-1" />Télécharger le modèle</Button>
        <Button size="sm" variant="outline-secondary" onClick={() => fileRef.current?.click()}><FaFileImport className="me-1" />Importer (Excel/CSV)</Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onImport} />
        <Button size="sm" variant="success" onClick={onExport} disabled={!results.length}><FaFileExcel className="me-1" />Exporter les résultats</Button>
        <Button size="sm" variant="outline-danger" onClick={onClear} disabled={!results.length}><FaTrash className="me-1" />Vider la liste</Button>

        {/* ⑤ Sélecteur de colonnes */}
        <Dropdown autoClose="outside" className="ms-auto">
          <Dropdown.Toggle size="sm" variant="outline-secondary" id="col-toggle-dd">
            <FaColumns className="me-1" />Colonnes
            <Badge bg="secondary" className="ms-1">{visCount}/{COMPUTED_COLS.length}</Badge>
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ padding: '8px 14px', minWidth: 170 }}>
            <div className="small fw-bold text-muted mb-1">Colonnes calculées</div>
            {COMPUTED_COLS.map(col => (
              <Form.Check
                key={col.key}
                type="checkbox"
                id={`col-${col.key}`}
                label={col.label}
                checked={visibleCols.has(col.key)}
                onChange={() => toggleCol(col.key)}
                className="small py-1"
              />
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* ── Filtres ── */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body className="py-2">
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label className="small fw-bold mb-1"><FaSearch className="me-1" />Recherche</Form.Label>
              <InputGroup size="sm">
                <Form.Control placeholder="Nom..." value={search} onChange={(e) => sf(setSearch)(e.target.value)} />
                {search && <Button variant="outline-secondary" onClick={() => sf(setSearch)('')}><FaTimes /></Button>}
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-bold mb-1">Statut</Form.Label>
              <Form.Select size="sm" value={statutFilter} onChange={(e) => sf(setStatutFilter)(e.target.value)}>
                <option value="">Tous</option>
                <option value="M">Mensuel (M)</option>
                <option value="J">Horaire (J)</option>
              </Form.Select>
            </Col>
            <Col md={3} className="d-flex align-items-end gap-2">
              <Form.Check
                type="checkbox"
                label="Écarts > 1 DH"
                checked={ecartOnly}
                onChange={(e) => sf(setEcartOnly)(e.target.checked)}
                className="small"
              />
              {ecartOnly && (
                <Button size="sm" variant="link" className="p-0 text-secondary small" onClick={() => sf(setEcartOnly)(false)}>
                  <FaTimes /> Tout afficher
                </Button>
              )}
              {smigOnly && (
                <Button size="sm" variant="link" className="p-0 text-danger small" onClick={() => sf(setSmigOnly)(false)}>
                  <FaTimes /> Tout afficher (SMIG)
                </Button>
              )}
              {missingOnly && (
                <Button size="sm" variant="link" className="p-0 small" style={{ color: '#e65100' }} onClick={() => sf(setMissingOnly)(false)}>
                  <FaTimes /> Tout afficher (manquants)
                </Button>
              )}
            </Col>
            <Col md={2} className="text-end">
              <Badge bg={filtered.length < results.length ? 'warning' : 'secondary'} text="dark" className="py-2 px-3">
                {filtered.length} / {results.length} ligne{results.length !== 1 ? 's' : ''}
              </Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Tableau ── */}
      <div className="table-container" style={{ overflowX: 'auto', maxHeight: '62vh', overflowY: 'auto' }}>
        <table className="table table-hover table-striped mb-0">
          <thead>
            <tr>
              <th style={thStyle}>#</th>
              <th style={{ ...thInputStyle, minWidth: 140 }}>Nom</th>
              <th style={{ ...thInputStyle, minWidth: 120 }}>Statut</th>
              <th style={{ ...thInputStyle, minWidth: 100 }}>Net cible</th>
              <th style={{ ...thInputStyle, minWidth: 90 }}>Ancienneté</th>
              <th style={{ ...thInputStyle, minWidth: 90 }} title="Nombre de jours travaillés sur le mois (26 = mois complet)">Jours trav.</th>
              <th style={{ ...thInputStyle, minWidth: 90 }} title="Prêt / avances salarié — déductible de l'IR">Autres ded.</th>
              <th style={{ ...thInputStyle, minWidth: 90 }} title="Cotisation mutuelle salarié — réduit le net mais reste imposable (IR)">Mutuelle sal.</th>
              <th style={{ ...thInputStyle, minWidth: 90 }} title="Conjoint + enfants — réduit l'IR (LF 2026)">Pers. charge</th>
              <th style={{ ...thInputStyle, minWidth: 80 }} title="10% du brut — cadres dirigeants (à activer dans Paramètres)">Représ.</th>
              <th style={{ ...thInputStyle, minWidth: 70 }} title="Cotisation CIMR — déductible IR (à activer dans Paramètres)">CIMR</th>
              <th style={{ ...thInputStyle, minWidth: 100 }} title="Avantages en nature (logement, véhicule...) — imposables, augmentent l'IR sans augmenter le net">Avant. nature</th>
              <th style={{ ...thInputStyle, minWidth: 60, textAlign: 'center' }} title="Solde de Tout Compte (1=oui) — remplace Prime de rendement par Congé payé">STC</th>
              {COMPUTED_COLS.map(col => vis(col.key) && <th key={col.key} style={thStyle}>{col.label}</th>)}
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {paged.map((r, idx) => {
              const isEmpty  = !r.net_cible && r.net_cible !== 0;
              const ecartHigh = !isEmpty && Math.abs(r.ecart) > 1;
              const rowNum   = (currentPage - 1) * pageSize + idx + 1;
              const inStyle  = {};
              return (
                <tr key={r.id} style={ecartHigh ? { background: '#fff5f5' } : undefined}>
                  <td className="text-muted small">{rowNum}</td>
                  <td style={{ minWidth: 140 }}>
                    <Form.Control size="sm" value={r.nom} onChange={(e) => onUpdate(r.id, 'nom', e.target.value)} placeholder="Nom" style={inStyle} />
                  </td>
                  <td style={{ minWidth: 120 }}>
                    <Form.Select size="sm" value={r.statut} onChange={(e) => onUpdate(r.id, 'statut', e.target.value)} style={inStyle}>
                      <option value="M">Mensuel (M)</option>
                      <option value="J">Horaire (J)</option>
                    </Form.Select>
                  </td>
                  <td style={{ minWidth: 100 }}>
                    <Form.Control size="sm" type="number" value={r.net_cible} onChange={(e) => onUpdate(r.id, 'net_cible', e.target.value)} placeholder="DH" style={inStyle}
                      title={r.belowSmig ? `Net cible inférieur au SMIG net proratisé (${fmt2(r.smigSeuil)} DH pour ${r.jours_travailles} j)` : r.missingNet ? 'Net cible manquant' : undefined}
                      className={r.belowSmig ? 'border-danger' : undefined}
                      style={r.missingNet && !r.belowSmig ? { borderColor: '#fb8c00' } : undefined} />
                    {r.belowSmig && <div className="small text-danger mt-1">⚠ &lt; SMIG</div>}
                    {r.missingNet && !r.belowSmig && <div className="small mt-1" style={{ color: '#e65100' }}>⚠ manquant</div>}
                  </td>
                  <td style={{ minWidth: 90 }}>
                    <Form.Control size="sm" type="number" step="0.01" value={r.anciennete} onChange={(e) => onUpdate(r.id, 'anciennete', e.target.value)} style={inStyle} />
                  </td>
                  <td style={{ minWidth: 90 }}>
                    <Form.Control size="sm" type="number" min="0" max="26" step="1" value={r.jours_travailles} onChange={(e) => onUpdate(r.id, 'jours_travailles', e.target.value)} style={inStyle}
                      style={r.missingJours ? { borderColor: '#fb8c00' } : undefined}
                      title={r.missingJours ? 'Nombre de jours manquant' : undefined} />
                    {r.missingJours && <div className="small mt-1" style={{ color: '#e65100' }}>⚠ manquant</div>}
                  </td>
                  <td style={{ minWidth: 90 }}>
                    <Form.Control size="sm" type="number" step="0.01" value={r.autres_deductions} onChange={(e) => onUpdate(r.id, 'autres_deductions', e.target.value)} style={inStyle} />
                  </td>
                  <td style={{ minWidth: 90 }}>
                    <Form.Control size="sm" type="number" step="0.01" value={r.mutuelle_salarie} onChange={(e) => onUpdate(r.id, 'mutuelle_salarie', e.target.value)} style={inStyle} />
                  </td>
                  <td style={{ minWidth: 90 }}>
                    <Form.Control size="sm" type="number" min="0" max="6" step="1" value={r.personnes_charge} onChange={(e) => onUpdate(r.id, 'personnes_charge', e.target.value)} style={inStyle} />
                  </td>
                  <td style={{ minWidth: 80, textAlign: 'center' }}>
                    <Form.Check
                      type="checkbox"
                      checked={!!r.representation}
                      onChange={(e) => onUpdate(r.id, 'representation', e.target.checked)}
                      title="Indemnité de représentation (10% du brut)"
                    />
                  </td>
                  <td style={{ minWidth: 70, textAlign: 'center' }}>
                    <Form.Check
                      type="checkbox"
                      checked={!!r.cimr}
                      onChange={(e) => onUpdate(r.id, 'cimr', e.target.checked)}
                      title="Cotisation CIMR (déductible IR)"
                    />
                  </td>
                  <td style={{ minWidth: 100 }}>
                    <Form.Control size="sm" type="number" step="0.01" value={r.avantages_nature} onChange={(e) => onUpdate(r.id, 'avantages_nature', e.target.value)} style={inStyle} />
                  </td>
                  <td style={{ minWidth: 60, textAlign: 'center' }}>
                    <Form.Check type="checkbox" checked={!!r.stc} onChange={(e) => onUpdate(r.id, 'stc', e.target.checked ? 1 : 0)} title="Solde de Tout Compte" />
                  </td>
                  {vis('brut')            && <td className="fw-bold">{isEmpty ? dash : fmt2(r.brutDisplay)}</td>}
                  {vis('cnss')            && <td>{isEmpty ? dash : fmt2(r.cnss)}</td>}
                  {vis('amo')             && <td
                    style={r.statut === 'J' ? { background: 'rgba(13,110,253,0.06)', borderLeft: '2px solid rgba(13,110,253,0.18)' } : undefined}
                    title={r.statut === 'J' ? `Calculé sur le brut réel : ${fmt2(r.brut)} DH` : undefined}
                  >{isEmpty ? dash : fmt2(r.amo)}</td>}
                  {vis('fraisPro')        && <td>{isEmpty ? dash : fmt2(r.fraisPro)}</td>}
                  {vis('baseIR')          && <td>{isEmpty ? dash : fmt2(r.baseIR)}</td>}
                  {vis('ir')              && <td
                    style={r.statut === 'J' ? { background: 'rgba(13,110,253,0.06)', borderLeft: '2px solid rgba(13,110,253,0.18)' } : undefined}
                    title={r.statut === 'J' ? `Calculé sur le brut réel : ${fmt2(r.brut)} DH` : undefined}
                  >{isEmpty ? dash : fmt2(r.ir)}</td>}
                  {vis('cimrMontant')     && <td>{isEmpty ? dash : fmt2(r.cimrMontant)}</td>}
                  {vis('indTransport')    && <td>{isEmpty ? dash : fmt2(r.indTransport)}</td>}
                  {vis('indSalissure')    && <td>{isEmpty ? dash : fmt2(r.indSalissure)}</td>}
                  {vis('indOutillage')    && <td>{isEmpty ? dash : fmt2(r.indOutillage)}</td>}
                  {vis('indPanier')       && <td>{isEmpty ? dash : fmt2(r.indPanier)}</td>}
                  {vis('indCaisse')       && <td>{isEmpty ? dash : fmt2(r.indCaisse)}</td>}
                  {vis('indLait')         && <td>{isEmpty ? dash : fmt2(r.indLait)}</td>}
                  {vis('indTournee')      && <td>{isEmpty ? dash : fmt2(r.indTournee)}</td>}
                  {vis('indRepresentation') && <td>{isEmpty ? dash : fmt2(r.indRepresentation)}</td>}
                  {vis('indemnitesTotal') && <td>{isEmpty ? dash : fmt2(r.indemnitesTotal)}</td>}
                  {vis('primeRendement')       && (
                    <td>
                      {isEmpty ? dash : (
                        <>
                          {fmt2(r.primeRendement)}
                          {r.stc ? <div className="small" style={{ color: '#6c757d' }}>Congé payé</div> : null}
                        </>
                      )}
                    </td>
                  )}
                  {vis('netRecalcule')    && <td>{isEmpty ? dash : fmt2(r.netRecalcule)}</td>}
                  {vis('ecart')          && (
                    <td>
                      {isEmpty ? dash : (
                        <Badge bg={ecartHigh ? 'danger' : 'success'}>
                          {r.ecart > 0 ? '+' : ''}{fmt2(r.ecart)}
                        </Badge>
                      )}
                    </td>
                  )}
                  {vis('chargesPatronales') && <td>{isEmpty ? dash : fmt2(r.chargesPatronales)}</td>}
                  {vis('coutEmployeur')  && <td>{isEmpty ? dash : fmt2(r.coutEmployeur)}</td>}
                  <td className="text-nowrap">
                    {!isEmpty && (
                      <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => onPdf(r)} title="Bulletin PDF">
                        <FaFilePdf />
                      </Button>
                    )}
                    <Button size="sm" variant="outline-danger" onClick={() => onRemove(r.id)}><FaTrash /></Button>
                  </td>
                </tr>
              );
            })}
            {!paged.length && (
              <tr>
                <td colSpan={colCount} className="text-center text-muted py-3">
                  {results.length === 0
                    ? 'Aucun employé — ajoutez une ligne ou importez un fichier.'
                    : 'Aucun résultat pour ces filtres.'}
                </td>
              </tr>
            )}
          </tbody>

          {/* ② Ligne de totaux */}
          {filtered.length > 0 && (
            <tfoot>
              <tr style={{ background: '#ede8e2', fontWeight: 600, fontSize: '0.83rem' }}>
                <td colSpan={12} className="text-end pe-2" style={{ color: '#6d4e36' }}>
                  Totaux — {filtered.length} ligne{filtered.length !== 1 ? 's' : ''}
                </td>
                {vis('brut')            && <td>{fmt2(totals.brut)}</td>}
                {vis('cnss')            && <td>{fmt2(totals.cnss)}</td>}
                {vis('amo')             && <td>{fmt2(totals.amo)}</td>}
                {vis('fraisPro')        && <td>{fmt2(totals.fraisPro)}</td>}
                {vis('baseIR')          && <td>{fmt2(totals.baseIR)}</td>}
                {vis('ir')              && <td>{fmt2(totals.ir)}</td>}
                {vis('cimrMontant')     && <td>{fmt2(totals.cimrMontant)}</td>}
                {vis('indTransport')    && <td>{fmt2(totals.indTransport)}</td>}
                {vis('indSalissure')    && <td>{fmt2(totals.indSalissure)}</td>}
                {vis('indOutillage')    && <td>{fmt2(totals.indOutillage)}</td>}
                {vis('indPanier')       && <td>{fmt2(totals.indPanier)}</td>}
                {vis('indCaisse')       && <td>{fmt2(totals.indCaisse)}</td>}
                {vis('indLait')         && <td>{fmt2(totals.indLait)}</td>}
                {vis('indTournee')      && <td>{fmt2(totals.indTournee)}</td>}
                {vis('indRepresentation') && <td>{fmt2(totals.indRepresentation)}</td>}
                {vis('indemnitesTotal') && <td>{fmt2(totals.indemnitesTotal)}</td>}
                {vis('primeRendement')       && <td>{fmt2(totals.primeRendement)}</td>}
                {vis('netRecalcule')    && <td>{fmt2(totals.netRecalcule)}</td>}
                {vis('ecart')          && (
                  <td style={{ color: Math.abs(totals.ecart) > filtered.length ? '#dc3545' : '#198754' }}>
                    {totals.ecart > 0 ? '+' : ''}{fmt2(totals.ecart)}
                  </td>
                )}
                {vis('chargesPatronales') && <td>{fmt2(totals.chargesPatronales)}</td>}
                {vis('coutEmployeur')  && <td>{fmt2(totals.coutEmployeur)}</td>}
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>

        {/* ── Pagination ── */}
        {filtered.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderTop: '1px solid #ede9e4', background: '#faf9f7', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.78rem', color: '#888' }}>
                <strong style={{ color: '#4a505c' }}>
                  {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)}
                </strong>{' '}sur {filtered.length}
              </span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                style={{ height: 24, borderRadius: 4, border: '1px solid #d4c8bc', fontSize: '0.72rem', color: '#4a505c', background: '#fff', padding: '0 4px', cursor: 'pointer' }}
              >
                {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / page</option>)}
              </select>
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                {[{ l: '«', t: 1 }, { l: '‹', t: currentPage - 1 }, { l: '›', t: currentPage + 1 }, { l: '»', t: totalPages }].map(({ l, t }) => (
                  <button key={l} onClick={() => goPage(t)} disabled={t < 1 || t > totalPages || t === currentPage}
                    style={{ minWidth: 28, height: 26, border: '1px solid #d4c8bc', borderRadius: 5, background: t === currentPage ? '#6d4e36' : '#fff', color: t === currentPage ? '#fff' : '#4a505c', cursor: (t < 1 || t > totalPages || t === currentPage) ? 'default' : 'pointer', fontSize: '0.78rem', opacity: (t < 1 || t > totalPages) ? 0.3 : 1 }}>
                    {l}
                  </button>
                ))}
                <span style={{ fontSize: '0.78rem', color: '#888', padding: '0 4px' }}>page {currentPage}/{totalPages}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────

const NetBrutCalculator = ({ onBack }) => {
  const initialRef = useRef();
  if (initialRef.current === undefined) initialRef.current = loadSavedState();
  const initial = initialRef.current;

  const [tab,      setTab]      = useState('employes');
  const [params,   setParams]   = useState(() => mergeParams(initial?.params));
  const [bareme,   setBareme]   = useState(() => initial?.bareme ?? DEFAULT_BAREME_IR.map(row => [...row]));
  const [employes, setEmployes] = useState(() => {
    if (initial?.employes?.length) {
      nextId = Math.max(...initial.employes.map(e => Number(e.id) || 0)) + 1;
      return initial.employes;
    }
    return [blankEmploye()];
  });

  // Sauvegarde automatique (params, barème, employés) à chaque changement
  useEffect(() => {
    saveState({ params, bareme, employes });
  }, [params, bareme, employes]);

  const updateParam     = (key, value) => setParams(p => ({ ...p, [key]: Number(value) }));
  const updateIndemnite = (key, field, value) => setParams(p => ({
    ...p,
    indemnites: { ...p.indemnites, [key]: { ...p.indemnites[key], [field]: field === 'actif' ? value : Number(value) } },
  }));
  const updateRepresentation = (field, value) => setParams(p => ({
    ...p,
    representation: { ...p.representation, [field]: field === 'actif' ? value : Number(value) },
  }));
  const updateMutuellePatronale = (field, value) => setParams(p => ({
    ...p,
    mutuelle_patronale: { ...p.mutuelle_patronale, [field]: field === 'actif' ? value : Number(value) },
  }));
  const updateCimr = (field, value) => setParams(p => ({
    ...p,
    cimr: { ...p.cimr, [field]: field === 'actif' ? value : Number(value) },
  }));
  const updateChargePatronale = (key, field, value) => setParams(p => ({
    ...p,
    charges_patronales: { ...p.charges_patronales, [key]: { ...p.charges_patronales[key], [field]: field === 'actif' ? value : Number(value) } },
  }));
  const updateBaremeCell = (idx, col, value) =>
    setBareme(b => b.map((row, i) => i === idx ? row.map((c, j) => j === col ? Number(value) : c) : row));
  const resetParams = () => {
    setParams(structuredClone(DEFAULT_PARAMS));
    setBareme(DEFAULT_BAREME_IR.map(row => [...row]));
  };

  const addRow    = () => setEmployes(rows => [...rows, blankEmploye()]);
  const removeRow = (id) => setEmployes(rows => rows.filter(r => r.id !== id));
  const updateRow = (id, key, value) => setEmployes(rows => rows.map(r => r.id === id ? { ...r, [key]: value } : r));

  const results = useMemo(
    () => employes.map(emp => {
      const smigSeuil = params.net_smig * (Math.min(Number(emp.jours_travailles) || 26, 26) / 26);
      const hasNom = Boolean(emp.nom);
      return {
        ...emp,
        ...computeEmploye(emp, params, bareme),
        smigSeuil,
        belowSmig:    Number(emp.net_cible) > 0 && Number(emp.net_cible) < smigSeuil,
        missingNet:   hasNom && !emp.net_cible && emp.net_cible !== 0,
        missingJours: hasNom && emp.statut === 'J' && !Number(emp.jours_travailles),
      };
    }),
    [employes, params, bareme]
  );

  // ⑧ Détection paramètres modifiés
  const paramsModified = useMemo(() => {
    const d = DEFAULT_PARAMS; const p = params;
    if (p.cnss_taux !== d.cnss_taux || p.cnss_plafond_brut !== d.cnss_plafond_brut ||
        p.cnss_montant_max !== d.cnss_montant_max || p.amo_taux !== d.amo_taux ||
        p.fraispro_taux !== d.fraispro_taux || p.fraispro_plafond !== d.fraispro_plafond ||
        p.smig_brut !== d.smig_brut || p.net_smig !== d.net_smig ||
        p.charge_famille_montant !== d.charge_famille_montant || p.charge_famille_max !== d.charge_famille_max) return true;
    if (Object.entries(p.indemnites).some(([k, v]) => v.plafond !== d.indemnites[k].plafond || v.actif !== d.indemnites[k].actif)) return true;
    if (p.representation.taux !== d.representation.taux || p.representation.actif !== d.representation.actif) return true;
    if (p.mutuelle_patronale.taux !== d.mutuelle_patronale.taux || p.mutuelle_patronale.actif !== d.mutuelle_patronale.actif) return true;
    if (p.cimr.taux !== d.cimr.taux || p.cimr.plafond !== d.cimr.plafond || p.cimr.actif !== d.cimr.actif) return true;
    if (Object.entries(p.charges_patronales).some(([k, v]) => v.taux !== d.charges_patronales[k].taux || v.actif !== d.charges_patronales[k].actif)) return true;
    return bareme.some((row, i) => {
      const dr = DEFAULT_BAREME_IR[i];
      return row[1] !== dr[1] || row[2] !== dr[2] || (row[0] !== Infinity && row[0] !== dr[0]);
    });
  }, [params, bareme]);

  // ⑦ Confirmation avant retour
  const handleBack = () => {
    const hasData = employes.some(e => Number(e.net_cible) > 0);
    if (hasData && !window.confirm('Des données non exportées seront perdues. Continuer ?')) return;
    onBack();
  };

  // Vide la liste des employés (les données sont aussi sauvegardées en local)
  const clearAll = () => {
    if (!window.confirm('Vider toute la liste des employés ? Cette action est irréversible.')) return;
    setEmployes([blankEmploye()]);
  };

  // ── Modèle Excel ──
  const exportTemplate = () => {
    const mkRow = (nom, statut, net_cible, anciennete, jours_travailles, autres_deductions, mutuelle_salarie, personnes_charge, representation, cimr, avantages_nature, stc = 0) => ({
      'Nom': nom, 'Statut (M=Mensuel / J=Horaire)': statut,
      'Net cible (DH)': net_cible, 'Ancienneté (coefficient)': anciennete,
      'Jours travaillés (26=mois complet)': jours_travailles,
      'Autres déductions (prêt, avances...) (DH)': autres_deductions,
      'Mutuelle salarié (DH)': mutuelle_salarie,
      'Personnes à charge (LF 2026, max 6)': personnes_charge,
      'Représentation (1=oui, 0=non)': representation,
      'CIMR actif (1=oui, 0=non)': cimr,
      'Avantages en nature (DH)': avantages_nature,
      'STC (1=Solde Tout Compte, 0=non)': stc,
      'Brut': '', 'CNSS': '', 'AMO': '', 'Frais pro': '', 'Base IR': '', 'IR': '', 'CIMR (DH)': '',
      'Ind. Transport': '', 'Ind. Salissure': '', 'Ind. Outillage': '', 'Ind. Panier': '',
      'Ind. Caisse': '', 'Ind. Lait': '', 'Ind. Tournée': '', 'Ind. Représentation': '',
      'Total indemnités': '', 'Net recalculé': '', 'Écart': '', 'Charges patronales': '', 'Coût employeur': '',
    });
    const ws = XLSX.utils.json_to_sheet([
      mkRow('Exemple Mensuel', 'M', 5377, 1, 26, 0, 0, 0, 0, 0, 0),
      mkRow('Exemple Horaire', 'J', 3920, 1, 26, 0, 0, 0, 0, 0, 0),
      mkRow('Horaire 13 jours', 'J', 3920, 1, 13, 0, 0, 0, 0, 0, 0),
      mkRow('Avec prêt et 3 pers. à charge', 'M', 9662, 1, 26, 150, 0, 3, 0, 0, 0),
      mkRow('Avec mutuelle salarié', 'M', 6000, 1, 26, 0, 100, 0, 0, 0, 0),
      mkRow('Cadre dirigeant (représentation)', 'M', 15000, 1, 26, 0, 0, 0, 1, 0, 0),
      mkRow('Avec CIMR et avantage en nature', 'M', 7000, 1, 26, 0, 0, 0, 0, 1, 1000),
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Modèle');
    XLSX.writeFile(wb, 'modele_import_net_vers_brut.xlsx');
  };

  // ── Import Excel/CSV ──
  const handleImport = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb  = XLSX.read(ev.target.result, { type: 'binary' });
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });
        const norm = (s) => String(s).split('(')[0].toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '');
        const get  = (row, ...als) => { for (const a of als) { const k = Object.keys(row).find(k => norm(k) === norm(a)); if (k != null && row[k] !== '') return row[k]; } return ''; };
        const imported = raw.map(row => {
          const st = String(get(row, 'statut') || 'M').trim().toUpperCase();
          return {
            id: nextId++,
            nom:               get(row, 'nom', 'nom et prenom', 'nomprenom', 'employe', 'salarie') || '',
            statut:            st.startsWith('J') ? 'J' : 'M',
            net_cible:         Number(get(row, 'net_cible', 'net cible', 'valeur cible', 'objectif', 'net')) || '',
            anciennete:        Number(get(row, 'anciennete', 'ancienneté', 'coefficient', 'coeff', 'cc')) || 1,
            jours_travailles:  Number(get(row, 'jours_travailles', 'jours travailles', 'jours travaillés', 'jours', 'nb jours')) || 26,
            autres_deductions: Number(get(row, 'autres_deductions', 'autres deductions', 'pret', 'prêt', 'avances')) || 0,
            mutuelle_salarie:  Number(get(row, 'mutuelle_salarie', 'mutuelle salarie', 'mutuelle salarié', 'mutuelle')) || 0,
            personnes_charge:  Number(get(row, 'personnes_charge', 'personnes a charge', 'personnes à charge', 'charges de famille', 'nb personnes charge')) || 0,
            representation:    Number(get(row, 'representation', 'représentation')) === 1,
            cimr:              Number(get(row, 'cimr actif', 'cotisation cimr', 'cimr')) === 1,
            avantages_nature:  Number(get(row, 'avantages_nature', 'avantages en nature', 'avantage nature', 'avantages')) || 0,
            stc:               Number(get(row, 'stc', 'solde tout compte', 'soldettoutcompte')) === 1 ? 1 : 0,
          };
        }).filter(r => r.nom || r.net_cible);
        if (imported.length) setEmployes(imported);
      } catch { /* fichier illisible */ }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  // ── Export résultats ──
  const buildExportRows = (list) => list.map(r => ({
      'Nom': r.nom, 'Statut': r.statut,
      'Net cible': Number(r.net_cible) || 0, 'Ancienneté': r.anciennete,
      'Jours travaillés': r.jours_travailles,
      'Autres déductions': r.autres_deductions,
      'Mutuelle salarié': r.mutuelleSalarie,
      'Personnes à charge': r.personnes_charge,
      'Représentation': r.representation ? 1 : 0,
      'CIMR actif': r.cimr ? 1 : 0,
      'Avantages en nature': r.avantagesNature,
      'STC': r.stc || 0,
      'Brut': Number(r.brutDisplay.toFixed(2)), 'CNSS': Number(r.cnss.toFixed(2)),
      'AMO': Number(r.amo.toFixed(2)), 'Frais pro': Number(r.fraisPro.toFixed(2)),
      'Base IR': Number(r.baseIR.toFixed(2)), 'IR': Number(r.ir.toFixed(2)),
      'CIMR (DH)': Number(r.cimrMontant.toFixed(2)),
      'Ind. Transport': r.indTransport, 'Ind. Salissure': r.indSalissure,
      'Ind. Outillage': r.indOutillage, 'Ind. Panier': r.indPanier,
      'Ind. Caisse': r.indCaisse, 'Ind. Lait': r.indLait,
      'Ind. Tournée': r.indTournee, 'Ind. Représentation': r.indRepresentation,
      'Total indemnités': r.indemnitesTotal, 'Prime de rendement': r.primeRendement, 'Net recalculé': r.netRecalcule, 'Écart': r.ecart,
      'Charges patronales': Number(r.chargesPatronales.toFixed(2)),
      'Coût employeur': Number(r.coutEmployeur.toFixed(2)),
  }));

  const exportResults = () => {
    const ws = XLSX.utils.json_to_sheet(buildExportRows(results));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Net vers Brut');
    XLSX.writeFile(wb, `net_vers_brut_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportSmigResults = () => {
    const ws = XLSX.utils.json_to_sheet(buildExportRows(results.filter(r => r.belowSmig)));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sous SMIG');
    XLSX.writeFile(wb, `sous_smig_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // ── Export PDF — bulletin individuel ──
  const exportBulletinPdf = (r) => {
    const C_BROWN  = [109, 78, 54];
    const C_WHITE  = [255, 255, 255];
    const C_BLIGHT = [243, 237, 230];
    const C_TEXT   = [78, 57, 41];

    const doc   = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const mg    = 14;
    let y = 12;

    // En-tête
    doc.setFillColor(...C_BROWN);
    doc.rect(mg, y, pageW - 2 * mg, 14, 'F');
    doc.setTextColor(...C_WHITE);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('SHEMS PAIE  —  Bulletin de paie (simulation)', mg + 4, y + 6);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.text(`Généré le : ${new Date().toLocaleDateString('fr-FR')}`, pageW - mg - 3, y + 6, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const statutLabel = r.statut === 'J' ? 'Horaire (J)' : 'Mensuel (M)';
    doc.text(`Salarié : ${r.nom || '—'}   |   Statut : ${statutLabel}   |   Ancienneté : ${r.anciennete}   |   Jours travaillés : ${r.jours_travailles}`, mg + 4, y + 11.5);
    y += 14 + 6;

    // GAINS
    const gains = [['Salaire brut', Number(r.brut.toFixed(2))]];
    const indLabels = {
      indTransport: 'Transport', indSalissure: 'Salissure', indOutillage: 'Outillage',
      indPanier: 'Panier', indCaisse: 'Caisse', indLait: 'Lait', indTournee: 'Tournée',
      indRepresentation: 'Représentation',
    };
    Object.entries(indLabels).forEach(([key, label]) => {
      if (r[key] > 0) gains.push([`Indemnité — ${label}`, Number(r[key].toFixed(2))]);
    });
    if (r.avantagesNature > 0) gains.push(['Avantages en nature', Number(r.avantagesNature.toFixed(2))]);
    const totalGains = gains.reduce((s, [, v]) => s + v, 0);
    gains.push(['Total brut', Number(totalGains.toFixed(2))]);

    autoTable(doc, {
      startY: y,
      margin: { left: mg, right: mg },
      head: [['GAINS', 'Montant (DH)']],
      body: gains.map(([l, v]) => [l, v.toFixed(2)]),
      styles: { fontSize: 9, cellPadding: 2.5, textColor: C_TEXT, lineColor: [220, 210, 200], lineWidth: 0.2 },
      headStyles: { fillColor: C_BROWN, textColor: C_WHITE, fontStyle: 'bold', fontSize: 9 },
      columnStyles: { 1: { halign: 'right', cellWidth: 35 } },
      didParseCell: (data) => {
        if (data.row.index === gains.length - 1) data.cell.styles.fontStyle = 'bold';
      },
    });
    y = doc.lastAutoTable.finalY + 6;

    // RETENUES
    const retenues = [
      ['CNSS', r.cnss], ['AMO', r.amo],
    ];
    if (r.cimrMontant > 0) retenues.push(['CIMR', r.cimrMontant]);
    if (r.autres_deductions > 0) retenues.push(['Prêt / avances', Number(r.autres_deductions)]);
    if (r.mutuelleSalarie > 0) retenues.push(['Mutuelle salarié', r.mutuelleSalarie]);
    retenues.push(['Impôt sur le revenu (IR)', r.ir]);
    const totalRetenues = retenues.reduce((s, [, v]) => s + v, 0);
    retenues.push(['Total retenues', totalRetenues]);

    autoTable(doc, {
      startY: y,
      margin: { left: mg, right: mg },
      head: [['RETENUES', 'Montant (DH)']],
      body: retenues.map(([l, v]) => [l, v.toFixed(2)]),
      styles: { fontSize: 9, cellPadding: 2.5, textColor: C_TEXT, lineColor: [220, 210, 200], lineWidth: 0.2 },
      headStyles: { fillColor: C_BROWN, textColor: C_WHITE, fontStyle: 'bold', fontSize: 9 },
      columnStyles: { 1: { halign: 'right', cellWidth: 35 } },
      didParseCell: (data) => {
        if (data.row.index === retenues.length - 1) data.cell.styles.fontStyle = 'bold';
      },
    });
    y = doc.lastAutoTable.finalY + 6;

    // NET À PAYER
    doc.setFillColor(...C_BLIGHT);
    doc.rect(mg, y, pageW - 2 * mg, 12, 'F');
    doc.setDrawColor(...C_BROWN);
    doc.setLineWidth(0.4);
    doc.rect(mg, y, pageW - 2 * mg, 12, 'S');
    doc.setTextColor(...C_BROWN);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('NET À PAYER', mg + 4, y + 7.5);
    doc.setFontSize(13);
    doc.text(`${r.netRecalcule.toFixed(2)} DH`, pageW - mg - 4, y + 7.5, { align: 'right' });
    y += 12 + 8;

    // Footer informatif
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...C_TEXT);
    doc.text(`Base imposable (IR) : ${r.baseIR.toFixed(2)} DH`, mg + 4, y);
    y += 5;
    doc.text(`Charges patronales : ${r.chargesPatronales.toFixed(2)} DH`, mg + 4, y);
    y += 5;
    doc.text(`Coût employeur total : ${r.coutEmployeur.toFixed(2)} DH`, mg + 4, y);

    const safeNom = (r.nom || 'employe').replace(/[^a-z0-9]+/gi, '_');
    doc.save(`bulletin_${safeNom}.pdf`);
  };

  const filledCount = employes.filter(e => Number(e.net_cible) > 0).length;

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <div>
          <Button variant="link" className="ps-0 text-decoration-none" onClick={handleBack}>
            <FaArrowLeft className="me-1" />Retour aux outils
          </Button>
          <h4 className="mb-0 mt-1"><FaCalculator className="me-2" />Calcul Net cible → Brut</h4>
        </div>
        <div className="btn-group">
          <Button variant={tab === 'parametres' ? 'primary' : 'outline-primary'} onClick={() => setTab('parametres')}>
            Paramètres{paramsModified && (
              <Badge bg="warning" text="dark" className="ms-1" style={{ fontSize: '0.65rem' }}>modifiés</Badge>
            )}
          </Button>
          <Button variant={tab === 'employes' ? 'primary' : 'outline-primary'} onClick={() => setTab('employes')}>
            Employés{filledCount > 0 && (
              <Badge bg="light" text="dark" className="ms-1">{filledCount}</Badge>
            )}
          </Button>
        </div>
      </div>

      {tab === 'parametres' ? (
        <ParametresTab params={params} bareme={bareme} onParam={updateParam}
          onIndemnite={updateIndemnite} onRepresentation={updateRepresentation}
          onMutuellePatronale={updateMutuellePatronale} onCimr={updateCimr} onChargePatronale={updateChargePatronale}
          onBareme={updateBaremeCell} onReset={resetParams} />
      ) : (
        <EmployesTab results={results} onAdd={addRow} onRemove={removeRow} onUpdate={updateRow}
          onTemplate={exportTemplate} onImport={handleImport} onExport={exportResults}
          onExportSmig={exportSmigResults} onClear={clearAll} onPdf={exportBulletinPdf} netSmig={params.net_smig} />
      )}
    </div>
  );
};

export default NetBrutCalculator;
